/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_INVOICE: 'transition/request-payment-invoice',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  REQUEST_PAYMENT_AFTER_INQUIRY_INVOICE: 'transition/request-payment-after-inquiry-invoice',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CONFIRM_PAYMENT_INVOICE: 'transition/confirm-payment-invoice',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',
  EXPIRE_PAYMENT_INVOICE: 'transition/expire-payment-invoice',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  ACCEPT_INVOICE: 'transition/accept-invoice',
  DECLINE: 'transition/decline',
  DECLINE_INVOICE: 'transition/decline-invoice',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_ACCEPT_INVOICE: 'transition/operator-accept-invoice',
  OPERATOR_DECLINE: 'transition/operator-decline',
  OPERATOR_DECLINE_INVOICE: 'transition/operator-decline-invoice',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',
  EXPIRE_INVOICE: 'transition/expire-invoice',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',
  CANCEL_INVOICE: 'transition/cancel-invoice',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  COMPLETE_INVOICE: 'transition/complete-invoice',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  OPERATOR_COMPLETE_INVOICE: 'transition/operator-complete-invoice',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  REVIEW_1_BY_PROVIDER_INVOICE: 'transition/review-1-by-provider-invoice',
  REVIEW_2_BY_PROVIDER_INVOICE: 'transition/review-2-by-provider-invoice',
  REVIEW_1_BY_CUSTOMER_INVOICE: 'transition/review-1-by-customer-invoice',
  REVIEW_2_BY_CUSTOMER_INVOICE: 'transition/review-2-by-customer-invoice',
  EXPIRE_CUSTOMER_REVIEW_PERIOD_INVOICE: 'transition/expire-customer-review-period-invoice',
  EXPIRE_PROVIDER_REVIEW_PERIOD_INVOICE: 'transition/expire-provider-review-period-invoice',
  EXPIRE_REVIEW_PERIOD_INVOICE: 'transition/expire-review-period-invoice',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  PENDING_PAYMENT_INVOICE: 'pending-payment-invoice',
  PAYMENT_EXPIRED_INVOICE: 'payment-expired-invoice',
  PREAUTHORIZED_INVOICE: 'preauthorized-invoice',
  DECLINED_INVOICE: 'declined-invoice',
  ACCEPTED_INVOICE: 'accepted-invoice',
  EXPIRED_INVOICE: 'expired-invoice',
  CANCELED_INVOICE: 'canceled-invoice',
  DELIVERED_INVOICE: 'delivered-invoice',
  REVIEWED_INVOICE: 'reviewed-invoice',
  REVIEWED_BY_CUSTOMER_INVOICE: 'reviewed-by-customer-invoice',
  REVIEWED_BY_PROVIDER_INVOICE: 'reviewed-by-provider-invoice',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_INVOICE]: states.PENDING_PAYMENT_INVOICE,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_INVOICE]: states.PENDING_PAYMENT_INVOICE,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
    [states.PENDING_PAYMENT_INVOICE]: {
      on: {
        [transitions.EXPIRE_PAYMENT_INVOICE]: states.PAYMENT_EXPIRED_INVOICE,
        [transitions.CONFIRM_PAYMENT_INVOICE]: states.PREAUTHORIZED_INVOICE,
      },
    },

    [states.PAYMENT_EXPIRED_INVOICE]: {},
    [states.PREAUTHORIZED_INVOICE]: {
      on: {
        [transitions.DECLINE_INVOICE]: states.DECLINED_INVOICE,
        [transitions.OPERATOR_DECLINE_INVOICE]: states.DECLINED_INVOICE,
        [transitions.EXPIRE_INVOICE]: states.EXPIRED_INVOICE,
        [transitions.ACCEPT_INVOICE]: states.ACCEPTED_INVOICE,
        [transitions.OPERATOR_ACCEPT_INVOICE]: states.ACCEPTED_INVOICE,
      },
    },

    [states.DECLINED_INVOICE]: {},
    [states.EXPIRED_INVOICE]: {},
    [states.ACCEPTED_INVOICE]: {
      on: {
        [transitions.CANCEL_INVOICE]: states.CANCELED_INVOICE,
        [transitions.COMPLETE_INVOICE]: states.DELIVERED_INVOICE,
        [transitions.OPERATOR_COMPLETE_INVOICE]: states.DELIVERED_INVOICE,
      },
    },

    [states.CANCELED_INVOICE]: {},
    [states.DELIVERED_INVOICE]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD_INVOICE]: states.REVIEWED_INVOICE,
        [transitions.REVIEW_1_BY_CUSTOMER_INVOICE]: states.REVIEWED_BY_CUSTOMER_INVOICE,
        [transitions.REVIEW_1_BY_PROVIDER_INVOICE]: states.REVIEWED_BY_PROVIDER_INVOICE,
      },
    },

    [states.REVIEWED_BY_CUSTOMER_INVOICE]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER_INVOICE]: states.REVIEWED_INVOICE,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD_INVOICE]: states.REVIEWED_INVOICE,
      },
    },
    [states.REVIEWED_BY_PROVIDER_INVOICE]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER_INVOICE]: states.REVIEWED_INVOICE,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD_INVOICE]: states.REVIEWED_INVOICE,
      },
    },
    [states.REVIEWED_INVOICE]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  const defaultTransactions = [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);

  const invoiceTransactions =[
    transitions.ACCEPT_INVOICE,
    transitions.OPERATOR_ACCEPT_INVOICE,
    transitions.CANCEL_INVOICE,
    transitions.COMPLETE_INVOICE,
    transitions.OPERATOR_COMPLETE_INVOICE,
    transitions.CONFIRM_PAYMENT_INVOICE,
    transitions.DECLINE_INVOICE,
    transitions.OPERATOR_DECLINE_INVOICE,
    transitions.EXPIRE_INVOICE,
    transitions.REVIEW_1_BY_CUSTOMER_INVOICE,
    transitions.REVIEW_1_BY_PROVIDER_INVOICE,
    transitions.REVIEW_2_BY_CUSTOMER_INVOICE,
    transitions.REVIEW_2_BY_PROVIDER_INVOICE,
  ].includes(transition);
  
  return defaultTransactions || invoiceTransactions
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER, transition.REVIEW_1_BY_CUSTOMER_INVOICE, transitions.REVIEW_2_BY_CUSTOMER_INVOICE,].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER, transitions.REVIEW_1_BY_PROVIDER_INVOICE, transitions.REVIEW_2_BY_PROVIDER_INVOICE].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY, transitions.REQUEST_PAYMENT_INVOICE, transitions.REQUEST_PAYMENT_AFTER_INQUIRY_INVOICE].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
    transitions.COMPLETE_INVOICE,
    transitions.OPERATOR_COMPLETE_INVOICE,
    transitions.REVIEW_1_BY_CUSTOMER_INVOICE,
    transitions.REVIEW_1_BY_PROVIDER_INVOICE,
    transitions.REVIEW_2_BY_CUSTOMER_INVOICE,
    transitions.REVIEW_2_BY_PROVIDER_INVOICE,
    transitions.EXPIRE_REVIEW_PERIOD_INVOICE,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD_INVOICE,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD_INVOICE,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.DECLINE,
    transitions.EXPIRE_PAYMENT_INVOICE,
    transitions.EXPIRE_INVOICE,
    transitions.CANCEL_INVOICE,
    transitions.DECLINE_INVOICE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
